import {
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
} from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { AppLayout, AppLayoutPublico } from '@/layouts'
import type { LayoutDaRota, PermissaoDeRota, Rota } from '@/models'

import { AcessoNegado } from './AcessoNegado'

const LAYOUTS: Record<LayoutDaRota, ComponentType> = {
  app: AppLayout,
  publico: AppLayoutPublico,
}

const paginas = new Map<string, LazyExoticComponent<ComponentType<any>>>()

function carregarPagina(
  caminho: string
): LazyExoticComponent<ComponentType<any>> {
  let pagina = paginas.get(caminho)

  if (!pagina) {
    pagina = lazy(() => import(`../pages/${caminho}`))
    paginas.set(caminho, pagina)
  }

  return pagina
}

interface PaginaDaRotaProps {
  caminho: string
  titulo?: string
  props?: Record<string, unknown>
}

function PaginaDaRota({
  caminho,
  titulo,
  props,
}: PaginaDaRotaProps): JSX.Element {
  const { pathname } = useLocation()

  useEffect(() => {
    if (titulo) document.title = `${titulo} | Horus Web`
  }, [titulo])

  const Pagina = carregarPagina(caminho)

  return (
    <Suspense>
      <Pagina key={pathname} {...props} />
    </Suspense>
  )
}

function elementoDaRota(rota: Rota): ReactNode {
  if (rota.redirecionarPara) {
    return <Navigate to={rota.redirecionarPara} replace />
  }

  if (rota.pagina) {
    return (
      <PaginaDaRota
        caminho={rota.pagina}
        titulo={rota.titulo}
        props={rota.props}
      />
    )
  }

  if (rota.layout) {
    const Layout = LAYOUTS[rota.layout]
    return <Layout />
  }

  return undefined
}

function renderizarRotas(
  rotas: Rota[],
  temPermissao: RouterCreatorProps['temPermissao']
): ReactNode[] {
  return rotas
    .filter(
      rota => temPermissao(rota.permissoes ?? []) || !!rota.mensagemSemPermissao
    )
    .map((rota, indice) => {
      const permitida = temPermissao(rota.permissoes ?? [])

      const elemento =
        permitida || !rota.mensagemSemPermissao ? (
          elementoDaRota(rota)
        ) : (
          <AcessoNegado
            key={rota.path ?? indice}
            mensagem={rota.mensagemSemPermissao}
          />
        )

      return rota.index ? (
        <Route key="index" index element={elemento} />
      ) : (
        <Route key={rota.path ?? indice} path={rota.path} element={elemento}>
          {permitida &&
            rota.filhas &&
            renderizarRotas(rota.filhas, temPermissao)}
        </Route>
      )
    })
}

interface RouterCreatorProps {
  rotas: Rota[]
  temPermissao(permissoes: PermissaoDeRota[]): boolean
  rotaPadrao?: string
}

export function RouterCreator({
  rotas,
  temPermissao,
  rotaPadrao = '/',
}: RouterCreatorProps): JSX.Element {
  return (
    <Routes>
      {renderizarRotas(rotas, temPermissao)}
      <Route path="*" element={<Navigate to={rotaPadrao} />} />
    </Routes>
  )
}
